import { useState } from 'react'
import { useStore } from '@tanstack/react-store'
import { Pencil } from 'lucide-react'
import { Skeleton } from 'skullet/react'
import { create } from '@bufbuild/protobuf'
import { timestampFromDate } from '@bufbuild/protobuf/wkt'

import { NoteSchema, type Note } from '@/gen/cortado/v1/actions_pb'
import { UserSchema } from '@/gen/cortado/v1/users_pb'
import { cn, formatFullName } from '@/lib/utils'
import { organizationStore } from '@/stores/organization.store'
import { useInvalidateJobActivity } from '@/stores/pipeline.store'
import { Button } from '@/components/ui/button'
import { NoteSheet } from '@/components/notes/note-sheet'
import { UserAvatar } from '@/components/shared/user-avatar'
import { NOTE_BODY_CLASS } from '@/components/notes/note-body'
import { JobNoteComposer } from '@/components/pipeline/job-note-composer'
import { jobNoteIsLong, jobNoteTimeLabel } from '@/components/pipeline/job-detail-view'

interface JobNotesPanelProps {
  jobId: string
  notes: Note[]
  totalCount: number
  isLoading: boolean
  onShowMore: () => void
}

const SAMPLE_NOTES: Note[] = [
  'Client confirmed the tasting for next Thursday and wants a vegetarian option on the menu.',
  'Venue needs the final headcount two weeks out.',
].map((note, i) =>
  create(NoteSchema, {
    id: `sample-${i}`,
    note: `<p>${note}</p>`,
    createdAt: timestampFromDate(new Date(2026, 0, i + 1)),
    createdByUser: create(UserSchema, { firstName: 'Jordan', lastName: 'Rivera' }),
  }),
)

function NoteRow({ note, onEdit }: { note: Note; onEdit: () => void }) {
  const [expanded, setExpanded] = useState(false)
  const isLong = jobNoteIsLong(note.note)
  const name = formatFullName(note.createdByUser?.firstName, note.createdByUser?.lastName, 'Unknown')

  return (
    <li className="px-6 py-3">
      <div className="flex items-center gap-2">
        <UserAvatar firstName={note.createdByUser?.firstName} lastName={note.createdByUser?.lastName} size="sm" />
        <span className="min-w-0 flex-1 truncate text-sm font-medium">{name}</span>
        <span className="font-mono text-xs text-muted-foreground">{jobNoteTimeLabel(note.createdAt)}</span>
        <Button type="button" variant="ghost" size="icon-sm" aria-label="Edit note" onClick={onEdit}>
          <Pencil className="h-3.5 w-3.5" />
        </Button>
      </div>
      <div className="mt-1.5 cursor-pointer pl-8" onClick={onEdit}>
        <div
          className={cn(NOTE_BODY_CLASS, isLong && !expanded && 'line-clamp-6')}
          dangerouslySetInnerHTML={{ __html: note.note }}
        />
      </div>
      {isLong && (
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          className="mt-1 pl-8 text-xs text-muted-foreground hover:text-foreground"
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </li>
  )
}

/**
 * The Notes tab: the rich-text note box on top, then the job's notes newest
 * first. Editing an existing note opens the note sheet.
 */
export function JobNotesPanel({ jobId, notes, totalCount, isLoading, onShowMore }: JobNotesPanelProps) {
  const selectedOrganizationId = useStore(organizationStore, (state) => state.selectedOrganizationId)
  const invalidateJobActivity = useInvalidateJobActivity()
  const [editingNote, setEditingNote] = useState<Note | undefined>()

  return (
    <>
      <JobNoteComposer jobId={jobId} />

      <Skeleton
        data={isLoading ? null : notes}
        sample={SAMPLE_NOTES}
        label="Loading notes"
        empty={!isLoading && notes.length === 0}
        emptyMessage={<p className="border-t px-6 py-4 text-sm text-muted-foreground">No notes yet.</p>}
      >
        {(notes) => (
          <>
            <ul className="divide-y border-t">
              {notes.map((note) => (
                <NoteRow key={note.id} note={note} onEdit={() => setEditingNote(note)} />
              ))}
            </ul>
            {notes.length < totalCount && (
              <div className="border-t px-6 py-2.5 text-xs text-muted-foreground">
                <button type="button" onClick={onShowMore} className="hover:text-foreground">
                  Show more
                </button>
              </div>
            )}
          </>
        )}
      </Skeleton>

      {editingNote && selectedOrganizationId && (
        <NoteSheet
          layout="pipeline"
          open
          organizationId={selectedOrganizationId}
          parentType={editingNote.parentType}
          parentId={editingNote.parentId}
          note={editingNote}
          onSaved={() => void invalidateJobActivity()}
          onOpenChange={(open) => {
            if (!open) setEditingNote(undefined)
          }}
        />
      )}
    </>
  )
}
